import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Calendar, Video, CreditCard, Youtube, Check, AlertTriangle, X, Loader2, Link2, ExternalLink, ShieldCheck, Radio } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import YouTubeLiveCard from "./YouTubeLiveCard";

const ICONS = { google_calendar: Calendar, google_meet: Video, zoom: Video, stripe_connect: CreditCard, youtube: Youtube, youtube_live: Radio };
const OWNER_LABEL = { enhancer: "Your account", platform: "EYW platform", center: "Center account" };

const STATUS_UI = {
  connected: { label: "Connected", cls: "bg-[#5C8A72]/15 text-[#5C8A72]", Icon: Check },
  disconnected: { label: "Not connected", cls: "bg-[#F1EEE8] text-[#8FA89B]", Icon: X },
  attention: { label: "Needs attention", cls: "bg-[#D98E73]/20 text-[#B5794E]", Icon: AlertTriangle },
  in_progress: { label: "Setup in progress", cls: "bg-[#D98E73]/20 text-[#B5794E]", Icon: AlertTriangle },
  not_configured: { label: "Not available yet", cls: "bg-[#F1EEE8] text-[#8FA89B]", Icon: X },
};

function fmtDate(s) {
  if (!s) return "";
  try { return new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); } catch { return ""; }
}

export default function IntegrationsPanel({ onGoToTab }) {
  const [busy, setBusy] = useState("");
  const [showYtLive, setShowYtLive] = useState(false);
  const { data, refetch, isLoading } = useQuery({ queryKey: ["enh-integrations"], queryFn: () => api.get("/enhancer/me/integrations").then((r) => r.data) });

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const z = q.get("zoom"), g = q.get("gcal"), yl = q.get("yt_live");
    if (z === "connected") { toast.success("Zoom connected"); refetch(); }
    else if (z === "error") toast.error("We couldn't connect Zoom. Please try again.");
    if (g === "connected") { toast.success("Google Calendar connected"); refetch(); }
    else if (g === "error") toast.error("Could not connect Google Calendar. Please try connecting again.");
    if (yl === "connected") { toast.success("YouTube Live connected — you can now create live broadcasts!"); refetch(); setShowYtLive(true); }
    else if (yl === "error") toast.error("Could not connect YouTube Live. Please try again.");
    if (z || g || yl) window.history.replaceState({}, "", window.location.pathname);
  }, [refetch]);

  const doOAuthConnect = async (path) => {
    try { setBusy(path); const { data } = await api.get(path); window.location.href = data.url; }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Not configured yet"); setBusy(""); }
  };
  const doDisconnect = async (path) => {
    if (!window.confirm("Disconnect this integration? Your past appointments and records are kept.")) return;
    try { setBusy(path); await api.post(path); toast.success("Disconnected"); refetch(); } catch { toast.error("Could not disconnect"); } finally { setBusy(""); }
  };
  const doStripeOnboard = async () => {
    try { setBusy("stripe"); const { data } = await api.post("/enhancer/connect/onboard", { return_url: window.location.href }); window.location.href = data.url; }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Stripe unavailable"); setBusy(""); }
  };
  const doStripeDashboard = async () => {
    try { setBusy("stripe-dash"); const { data } = await api.post("/enhancer/connect/dashboard", {}); window.open(data.url, "_blank"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  const renderActions = (p) => {
    const connected = p.status === "connected";
    if (p.key === "stripe_connect") {
      return (
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={doStripeOnboard} disabled={busy === "stripe"} data-testid="int-stripe-onboard" className="eyw-btn !py-2 text-sm disabled:opacity-60">
            {busy === "stripe" ? <Loader2 size={15} className="animate-spin" /> : <Link2 size={15} />} {connected ? "Update payout details" : "Set up payouts"}
          </button>
          {p.account && <button onClick={doStripeDashboard} disabled={busy === "stripe-dash"} data-testid="int-stripe-dashboard" className="eyw-btn-ghost !py-2 text-sm"><ExternalLink size={14} /> Stripe dashboard</button>}
        </div>
      );
    }
    if (p.key === "youtube") {
      return <button onClick={() => onGoToTab?.("Media & Broadcasting")} data-testid="int-youtube-manage" className="eyw-btn-ghost !py-2 text-sm"><Youtube size={15} /> Manage videos</button>;
    }
    if (p.key === "youtube_live") {
      return (
        <div className="flex items-center gap-3 flex-wrap">
          {connected ? (
            <>
              <button onClick={() => setShowYtLive(!showYtLive)} data-testid="int-ytlive-manage" className="eyw-btn !py-2 text-sm">
                <Radio size={15} /> {showYtLive ? "Hide broadcasts" : "Manage broadcasts"}
              </button>
              <button onClick={() => doDisconnect(p.actions.disconnect)} disabled={busy === p.actions.disconnect} data-testid="int-ytlive-disconnect" className="text-sm text-[#B45151] hover:underline">Disconnect</button>
            </>
          ) : p.status === "not_configured" ? (
            <p className="text-xs text-[#8FA89B]">An admin needs to finish setting up YouTube Live before you can connect.</p>
          ) : (
            <button onClick={() => doOAuthConnect(p.actions.connect)} disabled={busy === p.actions.connect} data-testid="int-ytlive-connect" className="eyw-btn !py-2 text-sm disabled:opacity-60">
              {busy === p.actions.connect ? <Loader2 size={15} className="animate-spin" /> : <Radio size={15} />} Connect YouTube Live
            </button>
          )}
        </div>
      );
    }
    if (p.key === "google_meet") {
      return <p className="text-xs text-[#8FA89B] flex items-center gap-1"><Info size={12} /> Enabled automatically when Google Calendar is connected.</p>;
    }
    // google_calendar & zoom — OAuth connect/disconnect
    if (p.status === "not_configured") {
      return <p className="text-xs text-[#8FA89B]">An admin needs to finish setting up {p.name} before you can connect.</p>;
    }
    return connected ? (
      <button onClick={() => doDisconnect(p.actions.disconnect)} disabled={busy === p.actions.disconnect} data-testid={`int-${p.key}-disconnect`} className="text-sm text-[#B45151] hover:underline">Disconnect</button>
    ) : (
      <button onClick={() => doOAuthConnect(p.actions.connect)} disabled={busy === p.actions.connect} data-testid={`int-${p.key}-connect`} className="eyw-btn !py-2 text-sm disabled:opacity-60">
        {busy === p.actions.connect ? <Loader2 size={15} className="animate-spin" /> : <Link2 size={15} />} Connect {p.name}
      </button>
    );
  };

  if (isLoading) return <div className="p-10 text-center text-[#8FA89B]"><Loader2 className="animate-spin inline" /> Loading integrations…</div>;

  const providers = data?.providers || [];
  const warnings = data?.service_warnings || [];
  const ytLiveConnected = providers.find((p) => p.key === "youtube_live")?.status === "connected";

  return (
    <div data-testid="enh-integrations-panel">
      <div className="mb-6">
        <h2 className="font-serif text-2xl text-[#1A242B]">Integrations</h2>
        <p className="text-sm text-[#4A5D6B] mt-1 flex items-center gap-1.5"><ShieldCheck size={15} className="text-[#5C8A72]" /> Connect the services you use to run your business. We never see or store your passwords — connections use each provider's secure sign-in and can be disconnected anytime.</p>
      </div>

      {warnings.length > 0 && (
        <div className="eyw-card p-4 mb-6 border-l-4 border-l-[#D98E73] bg-[#D98E73]/5" data-testid="int-service-warnings">
          <p className="font-medium text-[#B5794E] flex items-center gap-2 mb-2"><AlertTriangle size={17} /> Some services aren't fully bookable yet</p>
          <ul className="space-y-1 text-sm text-[#4A5D6B]">
            {warnings.map((w, i) => (
              <li key={i} data-testid={`int-warning-${i}`}>• <b>{w.service}</b> needs <b>{w.needs}</b> — {w.reason}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid gap-4">
        {providers.map((p) => {
          const Icon = ICONS[p.key] || Link2;
          const su = STATUS_UI[p.status] || STATUS_UI.disconnected;
          return (
            <div key={p.key} className="eyw-card p-5" data-testid={`int-card-${p.key}`}>
              <div className="flex items-start gap-4">
                <span className="grid place-items-center h-11 w-11 rounded-xl bg-[#144F75]/8 text-[#144F75] shrink-0"><Icon size={20} /></span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-serif text-lg text-[#1A242B]">{p.name}</h3>
                    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${su.cls}`} data-testid={`int-status-${p.key}`}><su.Icon size={12} /> {su.label}</span>
                    <span className="text-[10px] uppercase tracking-wide text-[#8FA89B] bg-[#F1EEE8] px-2 py-0.5 rounded-full">{OWNER_LABEL[p.ownership] || p.ownership}</span>
                  </div>
                  <p className="text-sm text-[#4A5D6B] mt-0.5">{p.purpose}</p>
                  <div className="text-xs text-[#8FA89B] mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5">
                    {p.account && <span data-testid={`int-account-${p.key}`}>Account: {p.account}</span>}
                    {p.connected_at && <span>Connected {fmtDate(p.connected_at)}</span>}
                    {p.last_sync_at && <span>Last sync {fmtDate(p.last_sync_at)}</span>}
                  </div>
                  {p.last_error && <p className="text-xs text-[#B45151] mt-1" data-testid={`int-error-${p.key}`}>⚠ {p.last_error}</p>}
                  <div className="mt-3">{renderActions(p)}</div>
                </div>
              </div>
              {/* YouTube Live broadcast manager inline */}
              {p.key === "youtube_live" && showYtLive && ytLiveConnected && (
                <div className="mt-4 border-t border-[#E8E3DC] pt-4">
                  <YouTubeLiveCard />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Info({ size = 12, className = "" }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}><circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" /></svg>;
}
